// Considered: SphereTypeHeaderMolecule — rejected as the reuse target itself because it is typed
// to Schema + LineageResult; the Arc Editor Canvas header has no schema, no lineage, and its own
// breadcrumb/tabs shape. This factors out the structural shell (back button, glyph tile, title
// row, action slot, a below-title content slot) both headers share, so SphereTypeHeaderMolecule
// becomes this molecule's first consumer instead of a sibling fork. See spec Reuse Audit
// (arc-editor-canvas-v1.md) and the design README's request for one header implementation.
import React from 'react';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import './EditorHeaderMolecule.scss';

export interface EditorHeaderMoleculeProps {
  /**
   * BEM block class this instance renders under. Defaults to `editor-header`; a consumer with
   * its own existing stylesheet (e.g. `sphere-type-header`) passes its own block name so every
   * nested `__body` / `__title-row` / `__name` / `__actions` selector keeps matching unchanged.
   */
  blockClassName?: string;
  className?: string;
  'data-testid'?: string;
  /** Passed straight through as `data-region` on the header element. */
  region?: string;
  /** Leaves the editor for the surface it was opened from. Omitted renders no back button. */
  onBack?: () => void;
  backAriaLabel?: string;
  backTestId?: string;
  backDatum?: string;
  /**
   * The back button's corner treatment. The two prototypes differ: the arc canvas's `.sb-back`
   * (`arc-board.css:39`) carries `--radius-md`, so `icon-square` is its default; the type
   * composer's `.s-back` (`trail-composer.jsx:880`) has no rounding and passes `rectangle`.
   */
  backShape?: 'icon-square' | 'rectangle';
  /** The glyph tile, fully rendered by the caller (e.g. a `GlyphAtom` with its own datum/testid). */
  glyph: React.ReactNode;
  /** The identity name, rendered inside the `<h1>`. */
  title: React.ReactNode;
  titleDatum?: string;
  titleKind?: string;
  titleTestId?: string;
  /** Header action slot (Save / Publish / view switch), right-aligned beside the title. */
  actions?: React.ReactNode;
  /**
   * Where the action cluster sits. `title-row` (default) nests it beside the `<h1>`; `header`
   * makes it the header's own flex item, a sibling of `__body`, so its height never stretches
   * the title row's cross axis (`trail-composer.jsx:879-913` — the type composer's TRT-3
   * `header` region reads that way; the arc canvas's does not).
   */
  actionsPlacement?: 'title-row' | 'header';
  /** Content rendered below the title row — a breadcrumb, chips row, or tabs. */
  below?: React.ReactNode;
}

const EditorHeaderMolecule: React.FC<EditorHeaderMoleculeProps> = ({
  blockClassName = 'editor-header',
  className = '',
  'data-testid': dataTestId,
  region = 'header',
  onBack,
  backAriaLabel,
  backTestId,
  backDatum,
  backShape = 'icon-square',
  glyph,
  title,
  titleDatum,
  titleKind = 'heading',
  titleTestId,
  actions,
  actionsPlacement = 'title-row',
  below,
}) => {
  const block = blockClassName;
  const classes = [block, className].filter(Boolean).join(' ').trim();
  const actionsNode = actions ? (
    <div className={`${block}__actions`} data-container="header > actions">
      {actions}
    </div>
  ) : null;

  return (
    <header className={classes} data-testid={dataTestId ?? block} data-region={region}>
      {onBack && (
        <ButtonAtom
          variant="text"
          shape={backShape}
          size="small"
          icon="pi pi-arrow-left"
          aria-label={backAriaLabel}
          onClick={onBack}
          className={`${block}__back`}
          data-testid={backTestId}
          data-datum={backDatum}
          data-kind="button"
        />
      )}
      {glyph}
      <div className={`${block}__body`} data-container="header > group">
        <div className={`${block}__title-row`}>
          <h1
            className={`${block}__name`}
            data-testid={titleTestId}
            data-datum={titleDatum}
            data-kind={titleKind}
          >
            {title}
          </h1>
          {actionsPlacement === 'title-row' && actionsNode}
        </div>
        {below}
      </div>
      {actionsPlacement === 'header' && actionsNode}
    </header>
  );
};

EditorHeaderMolecule.displayName = 'EditorHeaderMolecule';

export default EditorHeaderMolecule;
